import { getEventPropsBuilder, toSnakeCase } from '../../utils';
import type { AmplitudeEventKeys } from '../../amplitude-properties.types';
import type { TrackingEvent } from '@/tracking/tracking-manager';
import type { RefObject } from 'react';
import { formatOneBasedPosition } from './game-selected';

type InfluencerVideoSelectedEvent = {
    module: 'video_tile_zone' | 'video_preview_carousel';
    position: number;
    videoTitle: string;
    zoneName: string;
    zonePosition: string;
    pageName: 'home' | 'influencer_hub';
};

export const getInfluencerVideoSelectedProps = getEventPropsBuilder(
    (e: InfluencerVideoSelectedEvent): Partial<AmplitudeEventKeys> => ({
        event: 'Influencer Video Selected',
        Module: e.module,
        'Zone Name': toSnakeCase(e.zoneName),
        Position: formatOneBasedPosition(String(e.position)),
        'Video Title': toSnakeCase(e.videoTitle),
        'Zone Position': formatOneBasedPosition(e.zonePosition),
        'Page Name': e.pageName
    })
);

export const getInfluencerVideoZoneTrackingData = (
    elementRef: RefObject<HTMLElement>
): { zoneName: string; zonePosition: string } => {
    const element = elementRef.current;
    if (!element) {
        return { zoneName: '', zonePosition: '' };
    }

    return {
        zoneName: element.closest('[data-t-zonename]')?.getAttribute('data-t-zonename') ?? '',
        zonePosition: element.closest('[data-t-zoneposition]')?.getAttribute('data-t-zoneposition') ?? ''
    };
};

type InfluencerVideoPlayerData = {
    videoTitle: string;
    videoId: string;
    totalDuration: number;
    percentage?: 25 | 50 | 75 | 100;
};

type EventConfig = {
    event: 'Influencer Video Auto Play Successful' | 'Influencer Video Milestone Played';
};

const EVENT_CONFIGS: Record<'autoPlay' | 'milestone', EventConfig> = {
    autoPlay: { event: 'Influencer Video Auto Play Successful' },
    milestone: { event: 'Influencer Video Milestone Played' }
};

const buildEventProps = getEventPropsBuilder(
    (config: EventConfig, data: InfluencerVideoPlayerData): Partial<AmplitudeEventKeys> => {
        const eventProps: Partial<AmplitudeEventKeys> = {
            event: config.event,
            Module: 'influencer_video_player',
            'Video Title': toSnakeCase(data.videoTitle),
            'Video ID': data.videoId,
            'Total Video Duration': Math.round(data.totalDuration),
            'Page Name': 'influencer_video'
        };

        if (config.event === 'Influencer Video Milestone Played') {
            eventProps.Percentage = data.percentage;
        }

        return eventProps;
    }
);

export const trackInfluencerVideoEvent = (
    eventType: 'autoPlay' | 'milestone',
    data: InfluencerVideoPlayerData
): { event: TrackingEvent } => {
    const config = EVENT_CONFIGS[eventType];
    const eventProps = buildEventProps(config, data);

    return {
        event: {
            destination: 'amplitude',
            eventProps
        }
    };
};
